import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import PublishNewData from './PublishNewData.json';
import ProductDetails from './ProductDetails';
import { AssetDetails } from './AssetDetails';
import { SLAandAlerts } from './SLAandAlerts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import heartLoading from "../../../assests/heartLoading.gif";
import preloader from "../../../assests/preloader1.gif";
import certifiedLogo from "../../../assests/certifiedLogo.png";

const PublishMain = ({ onPrevious, nextStep, id }) => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState('Product Details');
  const [expandedAssets, setExpandedAssets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publishData, setPublishData] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/publish-overview/${id}`)
      .then(res => {
        setPublishData(res.data);
        // console.log('PublishMain - Setting PublishNewData:', PublishNewData);
        // setPublishData(PublishNewData);
      })
      .catch(() => {
        setPublishData(null);
        toast.error('Failed to load publish data');
      })
      .finally(() => {
        setLoading(false);
        // setPublishData(PublishNewData);
      }
    );
  }, [id]);

  console.log('PublishMain - publishData:', publishData);
  console.log('PublishMain - selectedSection:', selectedSection);

  const handlePublish = async () => {
    if (!confirmationChecked) {
      toast.error('Please confirm that you have reviewed all details before publishing.');
      return;
    }
    
    if (!id) return;
    setPublishing(true);
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/publish/${id}`);
      toast.success("Publish successful!");
      setTimeout(() => {
        navigate('/Dashboard');
      }, 1200);
    } catch (err) {
      toast.error("Publish failed. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  const toggleAssets = () => {
    setExpandedAssets(!expandedAssets);
  };

  const getAssetNames = () => {
    if (!publishData?.AssetInfo) return [];
    return Object.keys(publishData.AssetInfo).map(assetKey => ({
      key: assetKey,
      name: publishData.AssetInfo[assetKey].Configuration?.assetName || assetKey
    }));
  };

  const isAssetSelected = () => {
    return selectedSection.startsWith('asset-');
  };

  const getSelectedAssetKey = () => {
    if (isAssetSelected()) {
      return selectedSection.replace('asset-', '');
    }
    return null;
  };

  const renderContent = () => {
    console.log('PublishMain - renderContent called for:', selectedSection);
    
    if (isAssetSelected()) {
      const assetKey = getSelectedAssetKey();
      const assetData = publishData?.AssetInfo?.[assetKey];
      if (assetData) {
        return <AssetDetails publishData={{ AssetInfo: { [assetKey]: assetData } }} productId={id} />;
      }
    }

    switch (selectedSection) {
      case 'Product Details':
        return <ProductDetails publishData={publishData} />;
      case 'SLA and Alerts':
        return <SLAandAlerts publishData={publishData} onDataRefresh={setPublishData} />;
      case 'Publish':
        return (
          <div className="flex flex-col items-center">
            <div className="max-w-2xl text-center">
              <div className="mb-4">
                <div className="text-4xl mb-2 text-center flex justify-center"><img src={certifiedLogo} alt="certifiedLogo" className="h-[65px]" /></div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Ready to Publish</h3>
                <p className="text-gray-600 mb-4">
                  You're about to publish your data product. Please review all the information in the previous sections to ensure everything is correct.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="text-base font-semibold text-blue-800 mb-3">Publishing Checklist</h4>
                <div className="space-y-2 text-left">
                  <div className="flex items-center space-x-3">
                    <div className=" h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                      <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-gray-700">Product Details configured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className=" h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                      <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-gray-700">Assets onboarded and profiled</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className=" h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                      <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-gray-700">Data Quality rules configured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className=" h-[10px] w-[10px] bg-green-100 border-2 border-green-500 rounded-full flex items-center justify-center">
                      <div className="w-[10px] h-[10px] bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-sm text-gray-700">SLA and Alerts configured</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="confirmation"
                    checked={confirmationChecked}
                    onChange={(e) => setConfirmationChecked(e.target.checked)}
                    className="mt-0.5  mt-[8px] text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="confirmation" className="text-left text-gray-700 cursor-pointer">
                    <span className="font-medium text-yellow-800 text-sm">I confirm that I have reviewed all the details above</span>
                    <br />
                    <span className="text-xs text-gray-600">
                      I understand that once published, the data product will be available to users and changes may require a new version.
                    </span>
                  </label>
                </div>
              </div>

              <button
                className={`px-6 py-3 text-base font-bold rounded-lg shadow-lg transition-all duration-200 ${
                  confirmationChecked
                    ? 'bg-[#0000a0] hover:bg-[#000080] text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={handlePublish}
                disabled={publishing || !id || !confirmationChecked}
              >
                {publishing ? "Publishing..." : "Publish Data Product"}
              </button>

              {!confirmationChecked && (
                <p className="text-xs text-gray-500 mt-2">
                  Please check the confirmation box above to proceed with publishing.
                </p>
              )}
            </div>
          </div>
        );
      default:
        return <ProductDetails publishData={publishData} />;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
      <div className="flex flex-col items-center space-y-3">
        <img src={preloader} alt="Loading..." width={200}/>
        <span className="text-gray-700 text-[20px]">
        Hang tight! The resources are loading...
        </span>
      </div>
    </div>
    );
  }

  const assetNames = getAssetNames();

  return (
    <div className="flex flex-col h-[75%] w-[95%] mx-auto p-6">
      <Toaster position="top-right" />
      
      <div className="flex">
        {/* Left Sidebar */}
        <div className="w-96 bg-white rounded-lg shadow-sm border border-gray-200 p-4 mr-0 flex-shrink-0">
          <div className="space-y-2">
            {/* Product Details */}
            <button
              onClick={() => setSelectedSection('Product Details')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                selectedSection === 'Product Details'
                  ? 'bg-[#0000a0] text-white'
                  : 'bg-white text-gray-900 border border-green-300 hover:bg-gray-50'
              }`}
            >
              Product Details
            </button>

            {/* Onboarded Assets - Parent */}
            <div>
              <button
                onClick={toggleAssets}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-between ${
                  isAssetSelected()
                    ? 'bg-[#0000a0] text-white'
                    : 'bg-white text-gray-900 border border-green-300 hover:bg-gray-50'
                }`}
              >
                <span>Onboarded Assets</span>
                {expandedAssets ? (
                  <ChevronDown size={16} className="transform transition-transform duration-200" />
                ) : (
                  <ChevronRight size={16} className="transform transition-transform duration-200" />
                )}
              </button>
              
              {/* Asset Sub-items */}
              {expandedAssets && (
                <div className="ml-4 mt-2 space-y-1">
                  {assetNames.map((asset) => (
                    <button
                      key={asset.key}
                      onClick={() => setSelectedSection(`asset-${asset.key}`)}
                      className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all duration-200 break-words ${
                        selectedSection === `asset-${asset.key}`
                          ? 'bg-[#0000a0] text-white'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <span className="whitespace-normal leading-relaxed">{asset.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* SLA and Alerts */}
            <button
              onClick={() => setSelectedSection('SLA and Alerts')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                selectedSection === 'SLA and Alerts'
                  ? 'bg-[#0000a0] text-white'
                  : 'bg-white text-gray-900 border border-green-300 hover:bg-gray-50'
              }`}
            >
              SLA and Alerts
            </button>

            {/* Publish */}
            <button
              onClick={() => setSelectedSection('Publish')}
              className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                selectedSection === 'Publish'
                  ? 'bg-[#0000a0] text-white'
                  : 'bg-white text-gray-900 border border-green-300 hover:bg-gray-50'
              }`}
            >
              Publish
            </button>
          </div>
        </div>

        {/* Main Content Area - Fixed 800px height */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6 ml-0 overflow-y-auto" style={{ height: '800px' }}>
          {isAssetSelected() && (
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {assetNames.find(asset => `asset-${asset.key}` === selectedSection)?.name || 'Asset Details'}
              </h2>
            </div>
          )}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PublishMain;